<!--
  Confirmation dialog in front of "Aktivieren" (step bar of the provision run).

  Shows the facts of the run BEFORE its calculated bookings become money and requires an explicit
  acknowledgement while errors are open. Every sentence about ERROR contracts is backed by backend
  code - see the class TSDoc of CpRunActivationConfirmDialogComponent for the file references.
  No form HTML: the only control besides the buttons is the acknowledgement checkbox.
-->
<p-dialog
        header="Provisionslauf aktivieren"
        [visible]="visible"
        (visibleChange)="handleVisibleChange($event)"
        (onShow)="handleShow()"
        [modal]="true"
        [draggable]="false"
        [resizable]="false"
        [style]="{width: '46rem'}"
        [breakpoints]="{'960px': '90vw'}"
        styleClass="cp-run-activation-confirm-dialog">

    @if (provisionRunDto) {
        <div class="flex flex-wrap align-items-center gap-2 mb-3">
            <span class="font-semibold">{{ provisionRunDto.name }}</span>
            @if (provisionRunDto.periodStartDate && provisionRunDto.periodEndDate) {
                <span class="text-sm text-600">
                    {{ provisionRunDto.periodStartDate | date:'dd.MM.yyyy' }} – {{ provisionRunDto.periodEndDate | date:'dd.MM.yyyy' }}
                </span>
            }
            @if (provisionRunDto.valutaDate) {
                <span class="text-sm text-600">· Valuta {{ provisionRunDto.valutaDate | date:'dd.MM.yyyy' }}</span>
            }
        </div>
    }

    <p-message severity="info" styleClass="w-full mb-3">
        <span>
            Die berechneten Buchungen dieses Laufs werden verbindlich auf die Abrechnungskonten
            gebucht und wirken danach auf die Salden. <strong>Nach der Aktivierung lässt sich dieser
            Lauf nicht mehr neu berechnen.</strong>
        </span>
    </p-message>

    @if (summaryState === 'loading') {

        <div class="grid">
            @for (placeholder of [1, 2, 3, 4, 5, 6]; track placeholder) {
                <div class="col-6 md:col-4">
                    <p-skeleton height="3.5rem"></p-skeleton>
                </div>
            }
        </div>

    } @else if (summaryState === 'loaded' && summary) {

        <!-- Figures -->
        <div class="grid surface-50 border-round-lg mx-0 mb-3">
            <div class="col-6 md:col-4 p-3">
                <div class="text-xs font-semibold text-500 uppercase mb-1">Berechnete Provision</div>
                <div class="font-bold text-900">{{ formatCurrency(summary.calculatedProvisionAmount) }}</div>
                @if (summary.calculatedProvisionAmount == null) {
                    <div class="text-xs text-500">nur für den jüngsten Lauf der Provisionsart verfügbar</div>
                }
            </div>

            <div class="col-6 md:col-4 p-3">
                <div class="text-xs font-semibold text-500 uppercase mb-1">Rücklagenzuführung</div>
                <div class="font-bold text-900">{{ formatCurrency(summary.reserveContributionAmount) }}</div>
            </div>

            <div class="col-6 md:col-4 p-3">
                <div class="text-xs font-semibold text-500 uppercase mb-1"
                     pTooltip="Vertragsbuchungen dieses Laufs im Status „berechnet“. Jede wird zusammen mit ihrer Buchung auf dem Abrechnungskonto aktiviert."
                     tooltipPosition="top">
                    Buchungen <i class="fa-regular fa-circle-question ml-1"></i>
                </div>
                <div class="font-bold text-900">{{ formatCount(summary.calculatedBookingCount) }}</div>
            </div>

            <div class="col-6 md:col-4 p-3">
                <div class="text-xs font-semibold text-500 uppercase mb-1">Verträge mit Fehler</div>
                <div class="font-bold"
                     [ngClass]="(summary.errorContractCount ?? 0) > 0 ? 'text-red-700' : 'text-900'">
                    {{ formatCount(summary.errorContractCount) }}
                </div>
                @if ((summary.openErrorMessageCount ?? 0) > 0 || (summary.openRunErrorMessageCount ?? 0) > 0) {
                    <div class="text-xs text-red-600">
                        {{ formatCount((summary.openErrorMessageCount ?? 0) + (summary.openRunErrorMessageCount ?? 0)) }}
                        offene Fehlermeldungen
                    </div>
                }
            </div>

            <div class="col-6 md:col-4 p-3">
                <div class="text-xs font-semibold text-500 uppercase mb-1"
                     pTooltip="Negativ gerechneter Provisionsbetrag, automatisch auf 0,00 € überschrieben. Der Fall muss fachlich geklärt werden."
                     tooltipPosition="top">
                    Offene Warnungen M-026 <i class="fa-regular fa-circle-question ml-1"></i>
                </div>
                <div class="font-bold"
                     [ngClass]="(summary.openM026WarningCount ?? 0) > 0 ? 'text-orange-700' : 'text-900'">
                    {{ formatCount(summary.openM026WarningCount) }}
                </div>
            </div>

            <div class="col-6 md:col-4 p-3">
                <div class="text-xs font-semibold text-500 uppercase mb-1">Zuletzt berechnet am</div>
                <div class="font-bold text-900">
                    @if (summary.lastCalculatedAt) {
                        {{ summary.lastCalculatedAt | date:'dd.MM.yyyy HH:mm' }}
                    } @else {
                        –
                    }
                </div>
            </div>
        </div>

        @if (hasNothingToActivate()) {
            <p-message severity="warn" styleClass="w-full mb-3">
                <span>
                    Dieser Lauf enthält keine berechneten Buchungen. Die Aktivierung setzt nur den
                    Status des Laufs weiter.
                </span>
            </p-message>
        }

        @if (hasOpenErrors()) {
            <p-message severity="error" styleClass="w-full mb-3">
                <div class="flex flex-column gap-2">
                    <span class="font-semibold">Es sind Fehler offen.</span>
                    @if ((summary.errorContractCount ?? 0) > 0) {
                        <span>
                            Für {{ formatCount(summary.errorContractCount) }}
                            {{ summary.errorContractCount === 1 ? 'Vertrag' : 'Verträge' }} mit Status
                            „Fehler“ hat dieser Lauf keine Buchungen erzeugt. Die Aktivierung bucht für
                            diese Verträge nichts; ihre bereits aktiven Buchungen bleiben unverändert.
                        </span>
                        <span>
                            Ein späterer Provisionslauf berechnet sie nur dann erneut, wenn sie dort
                            wieder in die Vertragsauswahl fallen – das ist nicht garantiert. Sollen sie
                            in diesem Lauf abgerechnet werden: Ursache beheben (Reiter „Meldungen“) und
                            vor der Aktivierung „Neu berechnen“.
                        </span>
                    }
                    @if ((summary.openRunErrorMessageCount ?? 0) > 0) {
                        <span>
                            Zusätzlich sind {{ formatCount(summary.openRunErrorMessageCount) }}
                            Fehlermeldungen auf Laufebene offen (Reiter „Lauf-Meldungen“).
                        </span>
                    }
                </div>
            </p-message>
        }

        @if (hasUnknownErrorFigures()) {
            <p-message severity="warn" styleClass="w-full mb-3">
                <span>
                    Die Fehlerzahlen dieses Laufs konnten nicht vollständig ermittelt werden. Prüfen
                    Sie die Reiter „Verträge“ und „Meldungen“ vor der Aktivierung.
                </span>
            </p-message>
        }

        @if (requiresAcknowledgement()) {
            <div class="flex align-items-center gap-2 p-3 border-round-lg bg-red-50">
                <p-checkbox
                        inputId="cpRunActivationAcknowledge"
                        [(ngModel)]="errorsAcknowledged"
                        [binary]="true">
                </p-checkbox>
                <label for="cpRunActivationAcknowledge" class="cursor-pointer text-900">
                    Ich habe die offenen Fehler geprüft und aktiviere den Lauf trotzdem.
                </label>
            </div>
        }
    }

    <ng-template #footer>
        <p-button
                label="Abbrechen"
                icon="fa-regular fa-xmark"
                severity="secondary"
                [text]="true"
                (onClick)="handleCancel()">
        </p-button>

        <p-button
                label="Ja, aktivieren"
                icon="fa-solid fa-check"
                severity="primary"
                [loading]="summaryState === 'loading'"
                [disabled]="!canAccept()"
                (onClick)="handleAccept()">
        </p-button>
    </ng-template>

</p-dialog>
